import { useEffect, useRef, useState } from 'react'

/* ============================================================
   Innhold – rediger tekst, lenker og priser her
   ============================================================ */
const CONTACT_EMAIL = 'post@bakspekteret.no'
const INSTAGRAM_HANDLE = 'bakspekteret'
const INSTAGRAM_URL = `https://instagram.com/${INSTAGRAM_HANDLE}`
const ORG_NR = ''                                       // TODO: fyll inn org.nr. (vises i footeren når det er satt)

// Lager en mailto-lenke med ferdig utfylt emne
const mailto = (subject) => `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`

// Byttes ut med lenke til bookingsystem når det er på plass
const BOOKING_URL = mailto('Forespørsel om veiledning')
const FIRST_SESSION_URL = mailto('Forespørsel om første samtale')
const TALK_URL = mailto('Forespørsel om foredrag')
const CONTACT_URL = mailto('Henvendelse via bakspekteret.no')

const NAV = [
  { id: 'hjem', label: 'Hjem' },
  { id: 'om-meg', label: 'Om meg' },
  { id: 'veiledning', label: 'Veiledning' },
  { id: 'foredrag', label: 'Foredrag' },
  { id: 'sporsmal', label: 'Spørsmål' },
  { id: 'kontakt', label: 'Kontakt' },
]

const TAGLINE = ['Autisme', 'Nevrodivergens', 'Kunnskap', 'Forståelse']

const TARGET_GROUPS = [
  {
    title: 'For enkeltpersoner',
    text: 'For deg som ønsker større forståelse for egne behov, reaksjoner og erfaringer, og støtte til å finne løsninger som fungerer i din hverdag.',
  },
  {
    title: 'For pårørende',
    text: 'For deg som ønsker å forstå barnet eller den du står nær bedre, og finne måter å skape mer trygghet og gode rammer rundt dem.',
  },
  {
    title: 'For fagpersoner',
    text: 'For deg som ønsker å drøfte en situasjon, få nye perspektiver og finne gode måter å møte og tilrettelegge for nevrodivergente barn, unge eller voksne.',
  },
]

// Ofte stilte spørsmål. Vises på siden og legges også inn som strukturerte data (FAQPage),
// slik at søkemotorer og AI-søk kan bruke dem direkte.
// TODO (Julie): Dette er utkast – les over, rett og bekreft alle svar før siden publiseres.
// Spesielt: om diagnose er nødvendig, og hvilken videoplattform som brukes.
const FAQ = [
  {
    q: 'Hva betyr nevrodivergensbekreftende?',
    a: 'Et nevrodivergensbekreftende perspektiv betyr å se autisme og annen nevrodivergens som naturlige variasjoner i hvordan mennesker tenker, sanser og opplever verden, ikke som noe som må fikses. Målet er ikke å få noen til å fremstå mindre autistiske, men å forstå behovene deres og skape omgivelser der de kan fungere og ha det godt.',
  },
  {
    q: 'Hvem passer veiledningen for?',
    a: 'Veiledningen passer for enkeltpersoner som ønsker å forstå egne behov og reaksjoner bedre, for pårørende som vil skape mer trygghet rundt barnet eller den de står nær, og for fagpersoner som vil drøfte en situasjon og finne gode måter å tilrettelegge på.',
  },
  {
    q: 'Trenger man en diagnose for å få veiledning?',
    a: 'Nei. Du trenger verken diagnose eller henvisning. Veiledningen tar utgangspunkt i situasjonen og det du opplever som vanskelig, enten det finnes en diagnose eller ikke.',
  },
  {
    q: 'Hvordan foregår veiledningen, og hva koster det?',
    a: 'Vi starter med en første samtale på 45 minutter som koster 549 kr. Der får du en introduksjon til hvordan jeg jobber, vi ser på hvilke utfordringer som er viktigst, og legger en plan for veien videre. Videre veiledningstimer varer i 60 minutter og koster 1 100 kr. All veiledning foregår digitalt via video, men det kan også være mulig å avtale fysisk veiledning eller hjemmebesøk i Oslo og omegn.',
  },
  {
    q: 'Kan du holde foredrag for personalet i en barnehage eller skole?',
    a: 'Ja. Foredragene tilpasses målgruppen og kan holdes for blant annet skoler, barnehager, foreldregrupper, organisasjoner og andre fagmiljøer. Send en forespørsel med litt om hvem foredraget er for, så finner vi et tema og en form som passer.',
  },
  {
    q: 'Hva er maskering?',
    a: 'Maskering betyr at en autistisk person skjuler eller demper autistiske trekk for å passe inn, for eksempel ved å etterligne andre, holde tilbake behov eller tvinge seg gjennom sanseinntrykk. Det kan se ut som at alt går fint, men det koster mye energi og kan over tid føre til utmattelse og stress.',
  },
]

const TOPICS = [
  'Autisme og nevrodivergens',
  'Stress, regulering og nervesystemet',
  'Trygghet og relasjoner',
  'Sensoriske behov og tilrettelegging',
  'Maskering og belastning',
  'Fra atferd til forståelse',
]

/* ============================================================
   Små komponenter
   ============================================================ */
function Butterfly({ size = 64, className = '' }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M32 20v30" />
      <path d="M30 16l2 4 2-4" />
      <path d="M32 24C26 12 13 7 9.5 11c-3.5 4.5 2 16 13 19.5-8.5 2-12 10-7.5 14.5 4.5 4 13.5-.5 17-11" />
      <path d="M32 24c6-12 19-17 22.5-13 3.5 4.5-2 16-13 19.5 8.5 2 12 10 7.5 14.5-4.5 4-13.5-.5-17-11" />
    </svg>
  )
}

function Arrow({ dir = 'right' }) {
  return (
    <span className="arrow" aria-hidden="true">
      {dir === 'down' ? '↓' : '→'}
    </span>
  )
}

// Liten rosa glitterhale bak musepekeren. Vises bare med mus, og ikke for dem som har
// slått av animasjoner i systemet sitt.
const SPARKLE_COLORS = ['#f08cb4', '#f5a9c8', '#e5739f', '#f9c4da', '#d4a5e6']

function SparkleTrail() {
  const ref = useRef(null)

  useEffect(() => {
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (!canHover || reduced) return

    const canvas = ref.current
    const ctx = canvas.getContext('2d')
    const particles = []
    let last = null
    let frame = null

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      canvas.width = window.innerWidth * dpr
      canvas.height = window.innerHeight * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()

    // Firtakket stjerne – ser mer ut som glitter enn en prikk
    const star = (x, y, r, rot) => {
      ctx.save()
      ctx.translate(x, y)
      ctx.rotate(rot)
      ctx.beginPath()
      for (let i = 0; i < 8; i++) {
        const len = i % 2 === 0 ? r : r * 0.32
        const a = (i * Math.PI) / 4
        ctx.lineTo(Math.cos(a) * len, Math.sin(a) * len)
      }
      ctx.closePath()
      ctx.fill()
      ctx.restore()
    }

    const tick = (now) => {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight)
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i]
        const t = (now - p.born) / p.life
        if (t >= 1) {
          particles.splice(i, 1)
          continue
        }
        p.x += p.vx
        p.y += p.vy
        p.vy += 0.03 // faller sakte
        p.rot += p.spin
        // Blinker litt mens den toner ut
        const twinkle = 0.75 + 0.25 * Math.sin(now / 60 + p.phase)
        ctx.globalAlpha = (1 - t) * twinkle
        ctx.fillStyle = p.color
        if (p.round) {
          ctx.beginPath()
          ctx.arc(p.x, p.y, p.size * 0.45 * (1 - t * 0.5), 0, Math.PI * 2)
          ctx.fill()
        } else {
          star(p.x, p.y, p.size * (1 - t * 0.6), p.rot)
        }
      }
      ctx.globalAlpha = 1
      frame = particles.length ? requestAnimationFrame(tick) : null
    }

    const spawn = (x, y) => {
      particles.push({
        x: x + (Math.random() - 0.5) * 6,
        y: y + (Math.random() - 0.5) * 6,
        vx: (Math.random() - 0.5) * 0.8,
        vy: Math.random() * 0.4 + 0.1,
        size: Math.random() * 4 + 4,
        rot: Math.random() * Math.PI,
        spin: (Math.random() - 0.5) * 0.1,
        phase: Math.random() * 10,
        round: Math.random() < 0.35,
        color: SPARKLE_COLORS[Math.floor(Math.random() * SPARKLE_COLORS.length)],
        born: performance.now(),
        life: 550 + Math.random() * 450,
      })
    }

    const onMove = (e) => {
      const { clientX: x, clientY: y } = e
      // Antall gnister følger hvor langt musen har flyttet seg, så halen blir jevn
      const dist = last ? Math.hypot(x - last.x, y - last.y) : 0
      last = { x, y }
      const count = Math.min(Math.floor(dist / 10), 4)
      for (let i = 0; i < count; i++) spawn(x, y)
      if (particles.length > 80) particles.splice(0, particles.length - 80)
      if (count && !frame) frame = requestAnimationFrame(tick)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('resize', resize)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('resize', resize)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [])

  return <canvas ref={ref} className="sparkle-trail" aria-hidden="true" />
}

// Toner inn elementer med klassen .reveal når de rulles inn i bildet
function useReveal() {
  useEffect(() => {
    const items = document.querySelectorAll('.reveal')
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible')
            observer.unobserve(e.target)
          }
        })
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
    )
    items.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])
}

function Eyebrow({ children, center }) {
  return <p className={`eyebrow${center ? ' center' : ''}`}>{children}</p>
}

function Tagline({ small }) {
  return (
    <p className={`tagline${small ? ' small' : ''}`}>
      {TAGLINE.map((word, i) => (
        <span key={word}>
          {word}
          {i < TAGLINE.length - 1 && <span className="dot">•</span>}
        </span>
      ))}
    </p>
  )
}

/* ============================================================
   Seksjoner
   ============================================================ */
function Header() {
  const [active, setActive] = useState('hjem')
  const [open, setOpen] = useState(false)

  // Marker aktiv lenke basert på hvilken seksjon som er synlig
  useEffect(() => {
    const sections = NAV.map((n) => document.getElementById(n.id)).filter(Boolean)
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => e.isIntersecting && setActive(e.target.id))
      },
      { rootMargin: '-45% 0px -50% 0px' }
    )
    sections.forEach((s) => observer.observe(s))
    return () => observer.disconnect()
  }, [])

  return (
    <header className="header">
      <div className="container header-inner">
        <a href="#hjem" className="brand-small">BakSpekteret</a>
        <button
          className="menu-toggle"
          aria-label="Meny"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <span /><span /><span />
        </button>
        <nav className={`nav${open ? ' open' : ''}`}>
          {NAV.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              className={active === item.id ? 'active' : ''}
              onClick={() => setOpen(false)}
            >
              {item.label}
            </a>
          ))}
        </nav>
      </div>
    </header>
  )
}

function Hero() {
  return (
    <section id="hjem" className="hero">
      <div className="container hero-inner">
        <Butterfly size={78} className="hero-logo" />
        <h1 className="wordmark">BakSpekteret</h1>
        <hr className="divider" />
        <p className="hero-lead">Trygghet først.</p>
        <p className="hero-sub">Veiledning og foredrag om autisme og nevrodivergens.</p>
        <div className="btn-row">
          <a href="#veiledning" className="btn btn-soft">Veiledning <Arrow dir="down" /></a>
          <a href="#foredrag" className="btn btn-soft">Foredrag <Arrow dir="down" /></a>
        </div>
      </div>
    </section>
  )
}

function About() {
  return (
    <section id="om-meg" className="split">
      <div className="split-image portrait reveal">
        <picture>
          <source
            type="image/avif"
            srcSet="/images/julie-600.avif 598w, /images/julie.avif 950w"
            sizes="(max-width: 900px) 100vw, 50vw"
          />
          <img
            src="/images/julie.jpg"
            srcSet="/images/julie-600.jpg 598w, /images/julie.jpg 950w"
            sizes="(max-width: 900px) 100vw, 50vw"
            width="950"
            height="1700"
            fetchpriority="high"
            alt="Julie, grunnlegger av BakSpekteret"
          />
        </picture>
      </div>
      <div className="split-text reveal">
        <Eyebrow>Om meg</Eyebrow>
        <h2>Hei, jeg heter Julie.</h2>
        <p>
          Jeg er utdannet pedagog med master i pedagogisk-psykologisk rådgivning og
          Tverrfaglig nevroutdanning fra RBUP, og har ti års erfaring fra arbeid med autistiske
          barn og unge, som lærer, veileder og avdelingsleder i spesialskolen.
        </p>
        <p>
          Gjennom BakSpekteret ønsker jeg å gjøre kunnskap om autisme og nevrodivergens mer
          tilgjengelig, nyansert og menneskelig. Jeg er særlig opptatt av å forstå hva som
          ligger bak det vi ser, og av hvordan trygghet, omgivelser og relasjoner påvirker
          muligheten til å fungere og ha det godt.
        </p>
        <p>
          I dag tilbyr jeg veiledning og foredrag for enkeltpersoner, pårørende og fagpersoner.
        </p>
      </div>
    </section>
  )
}

function Guidance() {
  return (
    <section id="veiledning" className="section tinted">
      <div className="container">
        <div className="reveal">
          <Eyebrow>Veiledning</Eyebrow>
          <h2>Personlig og faglig veiledning</h2>
        </div>
        <div className="intro reveal">
          <p>
            Noen ganger trenger man først og fremst et sted å sortere, forstå og se situasjonen
            med nye øyne.
          </p>
          <p>
            Jeg tilbyr individuell veiledning knyttet til autisme og nevrodivergens. Veiledningen
            tar utgangspunkt i den enkelte situasjonen, og sammen utforsker vi det som er
            vanskelig, hva som kan ligge bak, og hvilke tilpasninger eller neste steg som kan
            være hjelpsomme.
          </p>
        </div>

        <div className="columns">
          {TARGET_GROUPS.map((g) => (
            <div className="column reveal" key={g.title}>
              <h3>{g.title}</h3>
              <p>{g.text}</p>
            </div>
          ))}
        </div>

        <div className="first-session reveal">
          <h3>Første samtale</h3>
          <p className="meta-line">
            45 minutter <span className="dot">•</span> Digitalt <span className="dot">•</span> 549 kr
          </p>
          <p>
            En trygg start for veien videre. Du får en introduksjon til hvordan jeg jobber, vi
            ser sammen på hvilke utfordringer som er viktigst for deg, og legger en plan for det
            videre arbeidet.
          </p>
        </div>

        <div className="booking-row reveal">
          <a href={FIRST_SESSION_URL} className="btn btn-soft">Book første samtale <Arrow /></a>
          <div className="booking-meta">
            <p className="meta-line">
              Videre veiledning <span className="dot">•</span> 60 minutter <span className="dot">•</span> Digitalt <span className="dot">•</span> 1&nbsp;100 kr
            </p>
            <p className="meta-note">
              Det kan også være mulig å avtale fysisk veiledning/hjemmebesøk i Oslo og omegn.
              Ta kontakt for nærmere avtale.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

function Talks() {
  return (
    <section id="foredrag" className="split">
      <div className="split-image sunflower reveal">
        <picture>
          <source
            type="image/avif"
            srcSet="/images/solsikke-900.avif 900w, /images/solsikke.avif 1600w"
            sizes="(max-width: 900px) 100vw, 50vw"
          />
          <img
            src="/images/solsikke.jpg"
            srcSet="/images/solsikke-900.jpg 900w, /images/solsikke.jpg 1600w"
            sizes="(max-width: 900px) 100vw, 50vw"
            width="1600"
            height="2134"
            loading="lazy"
            decoding="async"
            alt="Solsikke som strekker seg mot en lys, skyet himmel"
          />
        </picture>
      </div>
      <div className="split-text reveal">
        <Eyebrow>Foredrag</Eyebrow>
        <h2>Kunnskap som skaper forståelse</h2>
        <p>
          Jeg holder foredrag om autisme, nevrodivergens, trygghet og tilrettelegging, med
          utgangspunkt i både forskning, faglig erfaring og et nevrodivergensbekreftende
          perspektiv.
        </p>
        <p>
          Foredragene tilpasses målgruppen og kan holdes for blant annet skoler, barnehager,
          foreldregrupper, organisasjoner og andre fagmiljøer.
        </p>
        <p className="list-label">Aktuelle temaer kan være:</p>
        <ul className="topics">
          {TOPICS.map((t) => <li key={t}>{t}</li>)}
        </ul>
        <p className="topics-note">Ønsker du et foredrag om et annet tema? Ta gjerne kontakt.</p>
        <a href={TALK_URL} className="btn btn-soft">
          Send forespørsel <Arrow />
        </a>
      </div>
    </section>
  )
}

function Faq() {
  // FAQPage-data for søkemotorer. < escapes slik at teksten aldri kan avslutte script-taggen.
  const jsonLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  }).replace(/</g, '\\u003c')

  return (
    <section id="sporsmal" className="section">
      <div className="container faq">
        <div className="reveal">
          <Eyebrow>Spørsmål og svar</Eyebrow>
          <h2>Ofte stilte spørsmål</h2>
        </div>
        <div className="faq-list reveal">
          {FAQ.map(({ q, a }) => (
            <details key={q} className="faq-item">
              <summary>
                <span>{q}</span>
                <span className="faq-icon" aria-hidden="true" />
              </summary>
              <p>{a}</p>
            </details>
          ))}
        </div>
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
    </section>
  )
}

function Contact() {
  return (
    <section id="kontakt" className="section cta">
      <div className="container center reveal">
        <Eyebrow center>Ta kontakt</Eyebrow>
        <h2>Hvordan kan jeg hjelpe?</h2>
        <p className="cta-lead">
          Du kan booke veiledning direkte, eller ta kontakt dersom du ønsker foredrag, faglig
          <br className="hide-sm" /> veiledning eller har spørsmål om et mulig samarbeid.
        </p>
        <div className="btn-row">
          <a href={BOOKING_URL} className="btn btn-solid">Book veiledning <Arrow /></a>
          <a href={CONTACT_URL} className="btn btn-outline">Ta kontakt <Arrow /></a>
        </div>
        <p className="cta-mail">
          Eller send en e-post direkte til <a href={CONTACT_URL}>{CONTACT_EMAIL}</a>
        </p>
      </div>
    </section>
  )
}

function Footer() {
  return (
    <footer className="footer">
      <div className="container footer-top">
        <div className="footer-brand">
          <span>BakSpekteret</span>
          <Butterfly size={24} />
        </div>
        <Tagline small />
        <div className="footer-icons">
          <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="3" y="3" width="18" height="18" rx="5" />
              <circle cx="12" cy="12" r="4" />
              <circle cx="17.5" cy="6.5" r="0.8" fill="currentColor" stroke="none" />
            </svg>
            <span>@{INSTAGRAM_HANDLE}</span>
          </a>
          <a href={CONTACT_URL}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="M3 7l9 6 9-6" />
            </svg>
            <span>{CONTACT_EMAIL}</span>
          </a>
        </div>
      </div>
      <div className="container footer-bottom">
        <span suppressHydrationWarning>
          © {new Date().getFullYear()} BakSpekteret v/ Julie Ruud-Lund ENK
          {ORG_NR && <> · Org.nr. {ORG_NR}</>}
        </span>
        <span>Designet med omtanke ♥</span>
      </div>
    </footer>
  )
}

export default function App() {
  useReveal()

  return (
    <>
      <SparkleTrail />
      <Header />
      <main>
        <Hero />
        <About />
        <Guidance />
        <Talks />
        <Faq />
        <Contact />
      </main>
      <Footer />
    </>
  )
}
